import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '@/components/Header';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import HowToUseSection from '@/components/HowToUseSection';
import { HELP_URLS } from '@/components/HelpBenefits';
import WhatsNewSection from '@/components/WhatsNewSection';
import { ArrowLeft, GitCompareArrows, ChevronDown, ChevronUp, TrendingUp, BarChart3, Utensils, Skull, Scale, BookOpen, CheckCircle, FileSpreadsheet, Layers, Target, MousePointer } from 'lucide-react';

export default function MultiProfileBenefits() {
  const navigate = useNavigate();
  const [isHowToOpen, setIsHowToOpen] = useState(true);
  const [isBenefitsOpen, setIsBenefitsOpen] = useState(true);

  return (
    <div className="min-h-screen bg-[#F3F4F6] dark:bg-[#0b1220] transition-colors duration-200">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate('/multi-profile')}
          className="mb-6 text-[#0891b2] hover:text-[#0e7490] hover:bg-[#0891b2]/10"
          data-testid="back-to-multi-profile"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Multi-Profile Comparison
        </Button>

        {/* Main Content Card */}
        <Card className="border-slate-200 shadow-lg">
          <CardHeader className="bg-gradient-to-r from-[#0891b2] to-[#0e7490] text-white rounded-t-lg px-4 py-3 sm:px-6 sm:py-4">
            <CardTitle className="text-lg sm:text-2xl flex items-center gap-2 sm:gap-3">
              <GitCompareArrows className="h-5 w-5 sm:h-7 sm:w-7" />
              Multi-Profile Comparison
            </CardTitle>
          </CardHeader>
          
          <CardContent className="p-4 sm:p-6 space-y-4">
            
            {/* How to Use Section - Collapsible */}
            <Collapsible open={isHowToOpen} onOpenChange={setIsHowToOpen}>
              <CollapsibleTrigger asChild>
                <button 
                  className="w-full flex items-center justify-between p-3 sm:p-4 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 transition-colors"
                  data-testid="howto-section-toggle"
                >
                  <div className="flex items-center gap-2 sm:gap-3">
                    <div className="p-2 bg-purple-100 rounded-full">
                      <MousePointer className="h-4 w-4 sm:h-5 sm:w-5 text-purple-600" />
                    </div>
                    <span className="font-semibold text-purple-700 text-sm sm:text-base">How to Use Multi-Profile Comparison</span>
                  </div>
                  {isHowToOpen ? (
                    <ChevronUp className="h-5 w-5 text-purple-600" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-purple-600" />
                  )}
                </button>
              </CollapsibleTrigger>
              
              <CollapsibleContent className="mt-3 space-y-4">
                {/* Step 1 */}
                <section className="bg-blue-50 border border-blue-200 rounded-lg p-4 sm:p-5">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm">
                      1
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <FileSpreadsheet className="h-4 w-4 text-blue-600" />
                        <h4 className="text-sm sm:text-base font-semibold text-blue-800">Save Spreadsheet Analyses First</h4>
                      </div>
                      <p className="text-xs sm:text-sm text-gray-700 leading-relaxed">
                        Before using Multi-Profile Comparison, you need to have saved at least two spreadsheet analyses via the <strong>Spreadsheet Growth Comparison</strong> tool. Each saved analysis becomes a selectable profile for comparison. The more cages you analyze and save, the richer your comparison options.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Step 2 */}
                <section className="bg-amber-50 border border-amber-200 rounded-lg p-4 sm:p-5">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-amber-600 text-white rounded-full flex items-center justify-center font-bold text-sm">
                      2
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <CheckCircle className="h-4 w-4 text-amber-600" />
                        <h4 className="text-sm sm:text-base font-semibold text-amber-800">Select Profiles to Compare</h4>
                      </div>
                      <p className="text-xs sm:text-sm text-gray-700 leading-relaxed">
                        On the Multi-Profile Comparison page, you'll see a table of all your saved spreadsheet uploads. Use the checkboxes to select <strong>2 to 10 profiles</strong> you want to compare. Each selected profile is assigned a unique color for easy identification across all charts and tables.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Step 3 */}
                <section className="bg-green-50 border border-green-200 rounded-lg p-4 sm:p-5">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold text-sm">
                      3
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <BarChart3 className="h-4 w-4 text-green-600" />
                        <h4 className="text-sm sm:text-base font-semibold text-green-800">Analyze the Comparison Charts</h4>
                      </div>
                      <p className="text-xs sm:text-sm text-gray-700 leading-relaxed mb-3">
                        After clicking <strong>"Compare"</strong>, the system generates three normalized charts plus a detailed comparison table:
                      </p>
                      <ul className="space-y-2 text-xs sm:text-sm text-gray-700">
                        <li className="flex items-start gap-2">
                          <TrendingUp className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                          <span><strong>Growth Comparison:</strong> SGR-interpolated growth curves showing weight progression over time, enabling direct comparison of growth performance between cages</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Utensils className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                          <span><strong>Accumulated Feed:</strong> Cumulative feed given per cage, revealing differences in feeding intensity and total feed investment</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Skull className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                          <span><strong>Accumulated Mortality:</strong> Cumulative mortality over time, helping identify cages with abnormal loss patterns</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </section>

                {/* Step 4 */}
                <section className="bg-[#0891b2]/5 border border-[#0891b2]/20 rounded-lg p-4 sm:p-5">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-[#0891b2] text-white rounded-full flex items-center justify-center font-bold text-sm">
                      4
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Scale className="h-4 w-4 text-[#0891b2]" />
                        <h4 className="text-sm sm:text-base font-semibold text-[#0891b2]">Review the Detailed Table</h4>
                      </div>
                      <p className="text-xs sm:text-sm text-gray-700 leading-relaxed">
                        The comparison table at the bottom provides a side-by-side summary of each profile including input fish count, input weight, latest weight, <strong>accumulated biological FCR (prior to missing fish intelligence)</strong>, total feed, mortality count and percentage, and days in water. The bFCR is color-coded: green (&lt;1.6), amber (1.6–2.0), and red (&gt;2.0) for quick assessment.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Tip */}
                <section className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 sm:p-5">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-indigo-100 rounded-full flex-shrink-0">
                      <Layers className="h-4 w-4 text-indigo-600" />
                    </div>
                    <div>
                      <h4 className="text-xs sm:text-sm font-semibold text-indigo-800 mb-1">Normalized X-Axis</h4>
                      <p className="text-xs sm:text-sm text-gray-700 leading-relaxed">
                        All charts use <strong>"Days Since Input"</strong> on the X-axis rather than calendar dates. This allows you to compare cages that were stocked on different dates on an equal footing, making direct performance comparison possible even across different production cycles.
                      </p>
                    </div>
                  </div>
                </section>
              </CollapsibleContent>
            </Collapsible>
            
            <WhatsNewSection />

            {/* Learn the Benefits Section - Collapsible */}
            <Collapsible open={isBenefitsOpen} onOpenChange={setIsBenefitsOpen}>
              <CollapsibleTrigger asChild>
                <button 
                  className="w-full flex items-center justify-between p-3 sm:p-4 bg-[#0891b2]/5 border border-[#0891b2]/20 rounded-lg hover:bg-[#0891b2]/10 transition-colors"
                  data-testid="benefits-toggle"
                >
                  <div className="flex items-center gap-2 sm:gap-3">
                    <div className="p-2 bg-[#0891b2]/10 rounded-full">
                      <BookOpen className="h-4 w-4 sm:h-5 sm:w-5 text-[#0891b2]" />
                    </div>
                    <span className="font-semibold text-[#0891b2] text-sm sm:text-base">Learn the Benefits of Multi-Profile Comparison</span>
                  </div>
                  {isBenefitsOpen ? (
                    <ChevronUp className="h-5 w-5 text-[#0891b2]" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-[#0891b2]" />
                  )}
                </button>
              </CollapsibleTrigger>
              
              <CollapsibleContent className="mt-3 space-y-4">
                {/* Introduction */}
                <section className="bg-slate-50 border border-slate-200 rounded-lg p-4 sm:p-5">
                  <h3 className="text-sm sm:text-base font-semibold text-slate-800 mb-2 sm:mb-3">
                    Why Use Multi-Profile Comparison?
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-700 leading-relaxed">
                    Multi-Profile Comparison brings all your cage data together in a single view. Instead of analyzing each cage in isolation, you can compare growth trajectories, feeding patterns, and mortality trends side by side. This cross-cage perspective reveals performance gaps and best practices that are invisible when looking at individual analyses.
                  </p>
                </section>

                {/* Key Benefits */}
                <section className="bg-blue-50 border border-blue-200 rounded-lg p-4 sm:p-5">
                  <h4 className="text-xs sm:text-sm font-semibold text-blue-800 mb-3">Key Benefits:</h4>
                  <ul className="space-y-2 text-xs sm:text-sm text-gray-700">
                    <li className="flex items-start gap-2">
                      <TrendingUp className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span><strong>Identify top and bottom performers:</strong> Instantly see which cages are growing fastest and which are lagging behind the group</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Utensils className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span><strong>Compare feed efficiency:</strong> Spot cages consuming significantly more or less feed relative to growth, helping optimize feed allocation</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Skull className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span><strong>Detect mortality outliers:</strong> Identify cages with abnormally high mortality compared to the rest of the site</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Scale className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span><strong>Compare biological FCR:</strong> The accumulated bFCR (prior to missing fish intelligence) is calculated for each profile, enabling direct feed efficiency comparison across cages</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Layers className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span><strong>Cross-cycle comparison:</strong> Normalized "Days Since Input" axis allows comparison of cages from different stocking dates and production cycles</span>
                    </li>
                  </ul>
                </section>

                {/* Questions It Answers */}
                <section className="bg-green-50 border border-green-200 rounded-lg p-4 sm:p-5">
                  <h4 className="text-xs sm:text-sm font-semibold text-green-800 mb-3">It helps answer:</h4>
                  <ul className="space-y-2 text-xs sm:text-sm text-gray-700">
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 font-bold">•</span>
                      <span>Which cage is growing the fastest and why?</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 font-bold">•</span>
                      <span>Are some cages consuming disproportionately more feed for the same growth?</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 font-bold">•</span>
                      <span>Is mortality concentrated in specific cages or evenly distributed?</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 font-bold">•</span>
                      <span>Which cages have the best and worst biological FCR?</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 font-bold">•</span>
                      <span>How does the current cycle compare to previous cycles at the same site?</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 font-bold">•</span>
                      <span>Are there environmental or management differences explaining performance gaps?</span>
                    </li>
                  </ul>
                </section>

                {/* Conclusion */}
                <section className="bg-[#0891b2]/5 border border-[#0891b2]/20 rounded-lg p-4 sm:p-5">
                  <p className="text-xs sm:text-sm text-[#0891b2] font-medium leading-relaxed">
                    Multi-Profile Comparison transforms isolated cage analyses into a site-wide performance overview, helping you make data-driven decisions about feed management, stocking strategies, and operational priorities across your entire farm.
                  </p>
                </section>
              </CollapsibleContent>
            </Collapsible>

            {/* How to Use Section - Standardized Component with YouTube placeholder */}
            <HowToUseSection 
              featureName="Multi-Profile Comparison"
              youtubeUrl={HELP_URLS.multiProfileComparison.youtube}
            />

            {/* CTA */}
            <div className="pt-4 border-t border-slate-200 flex justify-center">
              <Button
                onClick={() => navigate('/multi-profile')}
                className="bg-[#0891b2] hover:bg-[#0e7490] text-white px-6 sm:px-8 py-2 sm:py-3 text-sm sm:text-base"
                data-testid="start-comparison-btn"
              >
                <GitCompareArrows className="h-4 w-4 mr-2" />
                Start Multi-Profile Comparison
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
